/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from "react";
import { Td, Tr } from "@lightspeed/cirrus-table";
import { Button } from "@lightspeed/flame/Button";
import { Input } from "@lightspeed/flame/Input";
import { useDispatch, useSelector } from "react-redux";
import * as databaseActions from "../../../redux/actions/databaseActions";
import D365DimensionsModal from "../D365DimensionsModal";
import QuickBooksClassDepartmentField from "../QuickBooksClassDepartmentField";
import {
  formatD365DimensionTooltip,
  getD365DimensionMappingValue,
  hasD365DimensionMapping,
} from "../../../utils/d365Dimensions";

let paymentAccountDropdownId = 0;

const Row = ({
  item,
  accountsList = [],
  classList = [],
  d365DimensionsList = [],
  customersList = [],
  isD365Integration = false,
  hideClassDepartmentColumn = false,
}) => {
  const dispatch = useDispatch();
  const ui = useSelector((state) => state.ui);
  const userState = useSelector((state) => state.userState);
  const database = useSelector((state) => state.database);

  const [edit, setEdit] = useState(false);
  const [changed, setChanged] = useState(false);

  const [inputBox1, setInputBox1] = useState("");
  const [inputBox2, setInputBox2] = useState("");
  const [inputBox3, setInputBox3] = useState("");
  const [inputBox4, setInputBox4] = useState("");
  const [inputBox5, setInputBox5] = useState("");
  const [inputBox6, setInputBox6] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [mealMappings, setMealMappings] = useState([]);
  const [dimensionMapping, setDimensionMapping] = useState("");

  const normalAccount = item?.account_id ?? "";
  const normalAccountDescription = item?.account_description ?? "";
  const normalID = item?.over_short_id ?? "";
  const normalNAME = item?.over_short_name ?? "";
  const classDepartmentLocation = item?.class_department_location ?? "";
  const normalCustomer = item?.qb_customer_id ?? item?.customer_id ?? "";
  const normalDimensionMapping = getD365DimensionMappingValue(item);
  const mealPeriodSettings = ui.settings || {};
  const activeMealPeriods =
    database.currentCompany.type === "RESTOK" &&
    mealPeriodSettings.meal_period_enabled === "on"
      ? [1, 2, 3, 4]
          .map((index) => ({
            index,
            label: String(mealPeriodSettings[`meal_period_${index}_name`] || "").trim(),
          }))
          .filter((period) => period.label !== "")
      : [];
  const isMealPeriodMode = activeMealPeriods.length > 0;

  const parseMaybeJson = (value) => {
    if (!value) return null;
    if (typeof value === "object") return value;

    try {
      return JSON.parse(value);
    } catch (err) {
      return null;
    }
  };

  const getExistingMealMapping = (period) => {
    const rawMappings = parseMaybeJson(
      item?.meal_period_mapping || item?.meal_period_mappings || item?.meal_periods
    );
    const mappingList = Array.isArray(rawMappings)
      ? rawMappings
      : Array.isArray(rawMappings?.mappings)
      ? rawMappings.mappings
      : [];

    return (
      mappingList.find(
        (mapping) =>
          Number(
            mapping.key ??
              mapping.meal_period_key ??
              mapping.index ??
              mapping.meal_period_index ??
              mapping.period_index
          ) === period.index ||
          mapping.name === period.label ||
          mapping.meal_period_name === period.label
      ) || {}
    );
  };

  const buildMealMappings = () =>
    activeMealPeriods.map((period) => {
      const existing = getExistingMealMapping(period);
      const prefix = `meal_period_${period.index}`;

      return {
        key: period.index,
        mealPeriodKey: period.index,
        rowKey: `${item.company_id}-${item.paymenttype_id}-${period.index}`,
        index: period.index,
        name: period.label,
        accountId:
          existing.account_id ??
          existing.accountId ??
          item?.[`${prefix}_account_id`] ??
          normalAccount,
        accountDescription:
          existing.account_description ??
          existing.accountDescription ??
          item?.[`${prefix}_account_description`] ??
          normalAccountDescription,
        classDepartmentLocation:
          existing.class_department_location ??
          existing.classDepartmentLocation ??
          item?.[`${prefix}_class_department_location`] ??
          classDepartmentLocation,
        customerId:
          existing.qb_customer_id ??
          existing.customer_id ??
          existing.customerId ??
          item?.[`${prefix}_qb_customer_id`] ??
          normalCustomer,
        overShortId:
          existing.over_short_id ??
          existing.overShortId ??
          item?.[`${prefix}_over_short_id`] ??
          normalID,
        overShortName:
          existing.over_short_name ??
          existing.overShortName ??
          item?.[`${prefix}_over_short_name`] ??
          normalNAME,
      };
    });

  const [showDropdown, setShowDropdown] = useState(false);
  const accountDropdownOwnerRef = useRef(null);
  if (!accountDropdownOwnerRef.current) {
    paymentAccountDropdownId += 1;
    accountDropdownOwnerRef.current = `payment-account-${paymentAccountDropdownId}`;
  }
  const [searchText, setSearchText] = useState("");
  const searchInputRef = useRef(null);

  const [showClassDropdown, setShowClassDropdown] = useState(false);
  const [classSearchText, setClassSearchText] = useState("");
  const classSearchInputRef = useRef(null);

  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [customerSearchText, setCustomerSearchText] = useState("");
  const customerSearchInputRef = useRef(null);
  const [d365Modal, setD365Modal] = useState(null);

  useEffect(() => {
    if (showDropdown && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [showDropdown]);

  useEffect(() => {
    if (!showDropdown || typeof document === "undefined") return;

    const handleOutsideClick = (event) => {
      const dropdownOwner = event.target.closest(
        "[data-account-dropdown-owner]"
      );

      if (
        dropdownOwner?.getAttribute("data-account-dropdown-owner") !==
        accountDropdownOwnerRef.current
      ) {
        setShowDropdown(false);
        setSearchText("");
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [showDropdown]);

  useEffect(() => {
    if (showClassDropdown && classSearchInputRef.current) {
      classSearchInputRef.current.focus();
    }
  }, [showClassDropdown]);

  useEffect(() => {
    if (showCustomerDropdown && customerSearchInputRef.current) {
      customerSearchInputRef.current.focus();
    }
  }, [showCustomerDropdown]);

  const resetValues = () => {
    setInputBox1(normalAccount);
    setInputBox2(normalAccountDescription);
    setInputBox3(normalID);
    setInputBox4(normalNAME);
    setInputBox5(classDepartmentLocation);
    setInputBox6(normalCustomer);
    setCustomerId(item.customer_id ? String(item.customer_id) : "");
    setMealMappings(buildMealMappings());
    setDimensionMapping(normalDimensionMapping);
    setShowDropdown(false);
    setShowClassDropdown(false);
    setShowCustomerDropdown(false);
    setSearchText("");
    setClassSearchText("");
    setCustomerSearchText("");
    setChanged(false);
  };

  useEffect(() => {
    setInputBox1(normalAccount);
    setInputBox2(normalAccountDescription);
    setInputBox3(normalID);
    setInputBox4(normalNAME);
    setInputBox5(classDepartmentLocation);
    setInputBox6(normalCustomer);
    setCustomerId(item.customer_id ? String(item.customer_id) : "");
    setMealMappings(buildMealMappings());
    setDimensionMapping(normalDimensionMapping);

    setShowDropdown(false);
    setShowClassDropdown(false);
    setShowCustomerDropdown(false);
    setSearchText("");
    setClassSearchText("");
    setCustomerSearchText("");

    setEdit(false);

    setChanged(false);
  }, [
    item?.company_id,
    item?.paymenttype_id,
    normalAccount,
    normalAccountDescription,
    normalID,
    normalNAME,
    classDepartmentLocation,
    normalCustomer,
    normalDimensionMapping,
    item.customer_id,
    database.currentCompany?.type,
    database.currentShop?.shop_id,
    database.currentShop?.area_id,
    mealPeriodSettings.meal_period_enabled,
    mealPeriodSettings.meal_period_1_name,
    mealPeriodSettings.meal_period_2_name,
    mealPeriodSettings.meal_period_3_name,
    mealPeriodSettings.meal_period_4_name,
  ]);

  useEffect(() => {
    const noChanges =
      normalAccount === inputBox1 &&
      normalAccountDescription === inputBox2 &&
      normalID === inputBox3 &&
      normalNAME === inputBox4 &&
      classDepartmentLocation === inputBox5 &&
      normalCustomer === inputBox6;
    const dimensionChanges =
      isD365Integration && normalDimensionMapping !== dimensionMapping;

    const initialMealMappings = buildMealMappings();
    const mealChanges =
      isMealPeriodMode &&
      JSON.stringify(mealMappings) !== JSON.stringify(initialMealMappings);

    setChanged(!noChanges || mealChanges || dimensionChanges);
  }, [
    inputBox1,
    inputBox2,
    inputBox3,
    inputBox4,
    inputBox5,
    inputBox6,
    normalAccount,
    normalAccountDescription,
    normalID,
    normalNAME,
    classDepartmentLocation,
    normalCustomer,
    normalDimensionMapping,
    dimensionMapping,
    isMealPeriodMode,
    mealMappings,
    mealPeriodSettings.meal_period_enabled,
    mealPeriodSettings.meal_period_1_name,
    mealPeriodSettings.meal_period_2_name,
    mealPeriodSettings.meal_period_3_name,
    mealPeriodSettings.meal_period_4_name,
  ]);

  const filteredAccounts = accountsList.filter((acc) => {
    const id = (acc?.id ?? "").toString().toLowerCase();
    const desc = (acc?.description ?? "").toString().toLowerCase();
    const s = searchText.toLowerCase();
    return id.includes(s) || desc.includes(s);
  });

  const filteredClasses = (Array.isArray(classList) ? classList : classList?.classes || []).filter((cls) => {
    const id = (cls?.id ?? "").toString().toLowerCase();
    const desc = (cls?.description ?? "").toString().toLowerCase();
    const s = classSearchText.toLowerCase();
    return id.includes(s) || desc.includes(s);
  });
  const d365DimensionRows = isD365Integration
    ? d365DimensionsList.filter((cls) => cls.dimension_level)
    : [];
  const currentDimensionMapping = dimensionMapping || normalDimensionMapping;
  const settingsButtonStyle = {
    marginLeft: 6,
    border: "1px solid #cbd5e1",
    borderRadius: 6,
    background: "#fff",
    color: "#0f172a",
    cursor: "pointer",
    fontSize: 12,
    padding: "3px 6px",
  };
  const renderD365SettingsButton = (target) =>
    isD365Integration ? (
      <button
        type="button"
        style={settingsButtonStyle}
        title={formatD365DimensionTooltip(currentDimensionMapping) || "No dimensions mapped"}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setD365Modal(target);
        }}
      >
        {hasD365DimensionMapping(currentDimensionMapping) ? "Dimensions*" : "Dimensions"}
      </button>
    ) : null;
  const applyD365DimensionValue = (nextValue) => {
    if (d365Modal && typeof d365Modal === "object") {
      updateMealMapping(d365Modal.index, { classDepartmentLocation: nextValue });
    } else {
      handleUpdate(nextValue);
    }
  };
  const d365ModalElement = (
    <D365DimensionsModal
      open={!!d365Modal}
      value={currentDimensionMapping}
      dimensions={d365DimensionRows}
      onClose={() => setD365Modal(null)}
      onApply={applyD365DimensionValue}
    />
  );

  const filteredCustomers = customersList.filter((customer) => {
    const id = (customer?.id ?? "").toString().toLowerCase();
    const desc = (customer?.description ?? "").toString().toLowerCase();
    const s = customerSearchText.toLowerCase();
    return id.includes(s) || desc.includes(s);
  });

  const updateMealMapping = (periodIndex, changes) => {
    setMealMappings((prev) =>
      prev.map((mapping) =>
        mapping.index === periodIndex ? { ...mapping, ...changes } : mapping
      )
    );
    setChanged(true);
  };

  const getMealMappingsPayload = () =>
    mealMappings.map((mapping) => ({
      key: mapping.mealPeriodKey,
      meal_period_key: mapping.mealPeriodKey,
      meal_period_index: mapping.index,
      meal_period_name: mapping.name,
      account_id: mapping.accountId,
      account_description: mapping.accountDescription,
      class_department_location: mapping.classDepartmentLocation,
      qb_customer_id: mapping.customerId,
      customer_id: mapping.customerId,
      over_short_id: mapping.overShortId,
      over_short_name: mapping.overShortName,
    }));

  const handleUpdate = (dimensionMappingOverride) => {
    const d365DimensionsValue =
      typeof dimensionMappingOverride === "string"
        ? dimensionMappingOverride
        : currentDimensionMapping || "";
    const primaryMealMapping = isMealPeriodMode
      ? mealMappings.find((mapping) => mapping.index === 1)
      : null;
    const paymentAccountId = primaryMealMapping?.accountId ?? inputBox1;
    const paymentAccountName = primaryMealMapping?.accountDescription ?? inputBox2;
    const paymentClass = primaryMealMapping?.classDepartmentLocation ?? inputBox5;
    const paymentCustomer = primaryMealMapping?.customerId ?? inputBox6;
    const qbCustomerValue = (inputBox6 ?? "").toString().trim() || null;
    const internalCustomerId = (customerId ?? "").toString().trim() || null;

    if (database.currentCompany.type === "RETAIL") {
      dispatch(
        databaseActions.updateRetailPayment(
          userState.user.user_id,
          item.company_id,
          database.currentShop.shop_id,
          inputBox1,
          inputBox2,
          item.paymenttype_id,
          inputBox3,
          inputBox4,
          inputBox5,
          qbCustomerValue,
          internalCustomerId,
          null,
          null,
          d365DimensionsValue,
          databaseActions.getShopPayments(
            database.currentCompany.id,
            userState.user.user_id,
            database.currentShop.shop_id
          )
        )
      );
    }

    if (database.currentCompany.type === "RETAILX") {
      dispatch(
        databaseActions.updateRetailPaymentX(
          userState.user.user_id,
          item.company_id,
          database.currentShop.shop_id,
          inputBox1,
          inputBox2,
          item.paymenttype_id,
          inputBox3,
          inputBox4,
          inputBox5,
          qbCustomerValue,
          internalCustomerId,
          null,
          null,
          databaseActions.getShopPaymentsX(
            database.currentCompany.id,
            userState.user.user_id,
            database.currentShop.shop_id
          )
        )
      );
    }

    if (database.currentCompany.type === "RESTOK") {
      if (database.currentCompany.direct) {
        dispatch(
          databaseActions.updateRestoPaymentsK(
            userState.user.user_id,
            item.company_id,
            item.paymenttype_id,
            paymentAccountId,
            paymentAccountName,
            0,
            paymentClass,
            (paymentCustomer ?? "").toString().trim() || null,
            (paymentCustomer ?? "").toString().trim() || null,
            isMealPeriodMode ? getMealMappingsPayload() : null,
            d365DimensionsValue,
            databaseActions.patchCurrentAreaPayment(item.paymenttype_id, {
              account_id: paymentAccountId,
              account_description: paymentAccountName,
              class_department_location: paymentClass,
              qb_customer_id: (paymentCustomer ?? "").toString().trim() || null,
              customer_id: (paymentCustomer ?? "").toString().trim() || null,
              meal_period_mapping: isMealPeriodMode ? getMealMappingsPayload() : item.meal_period_mapping,
              meal_period_mappings: isMealPeriodMode ? getMealMappingsPayload() : item.meal_period_mappings,
              meal_periods: isMealPeriodMode ? getMealMappingsPayload() : item.meal_periods,
              d365_dimensions: d365DimensionsValue,
            })
          )
        );
      } else {
        dispatch(
          databaseActions.updateRestoPaymentsK(
            userState.user.user_id,
            item.company_id,
            item.paymenttype_id,
            paymentAccountId,
            paymentAccountName,
            database.currentShop.area_id,
            paymentClass,
            (paymentCustomer ?? "").toString().trim() || null,
            (paymentCustomer ?? "").toString().trim() || null,
            isMealPeriodMode ? getMealMappingsPayload() : null,
            d365DimensionsValue,
            databaseActions.patchCurrentAreaPayment(item.paymenttype_id, {
              account_id: paymentAccountId,
              account_description: paymentAccountName,
              class_department_location: paymentClass,
              qb_customer_id: (paymentCustomer ?? "").toString().trim() || null,
              customer_id: (paymentCustomer ?? "").toString().trim() || null,
              meal_period_mapping: isMealPeriodMode ? getMealMappingsPayload() : item.meal_period_mapping,
              meal_period_mappings: isMealPeriodMode ? getMealMappingsPayload() : item.meal_period_mappings,
              meal_periods: isMealPeriodMode ? getMealMappingsPayload() : item.meal_periods,
              d365_dimensions: d365DimensionsValue,
            })
          )
        );
      }
    }

    if (database.currentCompany.type === "RESTO") {
      if (database.currentCompany.direct) {
        dispatch(
          databaseActions.updateRestoPayments(
            userState.user.user_id,
            item.company_id,
            item.paymenttype_id,
            inputBox1,
            inputBox2,
            0,
            inputBox5,
            qbCustomerValue,
            internalCustomerId,
            databaseActions.getAreaPayments(
              userState.user.user_id,
              database.currentCompany.id,
              0
            )
          )
        );
      } else {
        dispatch(
          databaseActions.updateRestoPayments(
            userState.user.user_id,
            item.company_id,
            item.paymenttype_id,
            inputBox1,
            inputBox2,
            database.currentShop.area_id,
            inputBox5,
            qbCustomerValue,
            internalCustomerId,
            databaseActions.getAreaPayments(
              userState.user.user_id,
              database.currentCompany.id,
              database.currentShop.area_id
            )
          )
        );
      }
    }

    setEdit(false);
    setShowDropdown(false);
    setShowClassDropdown(false);
    setShowCustomerDropdown(false);
  };

  if (isMealPeriodMode) {
    const rows = mealMappings.length > 0 ? mealMappings : buildMealMappings();

    if (!edit) {
      return (
        <>
          {d365ModalElement}
          {rows.map((mapping, index) => (
            <Tr
              key={`${mapping.rowKey}-${database.currentShop?.area_id ?? 0}`}
              textAlign="right"
              className={`${ui.theme === "flame" ? "" : "dark-table-row"}`}
              onDoubleClick={() => setEdit(true)}
            >
              {index === 0 && (
                <>
                  <Td width="15%" className="td-pos" rowSpan={rows.length}>
                    {item.company_id}
                  </Td>
                  <Td width="15%" className="td-pos" rowSpan={rows.length}>
                    {item.paymenttype_id}
                  </Td>
                  <Td width="20%" className="td-pos td-pos-last" rowSpan={rows.length}>
                    {item.paymenttypeid_name}
                  </Td>
                </>
              )}

              <Td width="20%" className="td-acc td-acc-first">
                <div style={{ display: "grid", gridTemplateColumns: "12ch 1fr", alignItems: "center" }}>
                  <strong title={mapping.name} style={{ display: "block", maxWidth: "12ch", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {mapping.name}
                  </strong>
                  <span>{mapping.accountId}</span>
                </div>
              </Td>
              <Td width="20%" className="td-acc">{mapping.accountDescription}</Td>
              <Td width="20%" className="td-acc" style={{ display: hideClassDepartmentColumn ? "none" : undefined }}>
                {mapping.classDepartmentLocation}
                {index === 0 && renderD365SettingsButton("main")}
              </Td>
              <Td width="20%" className="td-acc">{mapping.customerId}</Td>
              <Td width="20%" className="td-acc">{mapping.overShortId}</Td>
              <Td width="20%" className="td-acc">{mapping.overShortName}</Td>

              {index === 0 && (
                <Td width="10%" textAlign="right" className="td-acc" rowSpan={rows.length}>
                  <div className="row-input-wrapper-end">
                    <Button onClick={() => setEdit(true)} size="small" fill="true">
                      Edit
                    </Button>
                  </div>
                </Td>
              )}
            </Tr>
          ))}
        </>
      );
    }

    return (
      <>
        {d365ModalElement}
        {rows.map((mapping, index) => (
          <Tr
            key={`${mapping.rowKey}-${database.currentShop?.area_id ?? 0}`}
            textAlign="right"
            className={`${ui.theme === "flame" ? "" : "dark-table-row"}`}
            onDoubleClick={() => setEdit(true)}
          >
            {index === 0 && (
              <>
                <Td width="15%" className="td-pos" rowSpan={rows.length}>
                  {item.company_id}
                </Td>

                <Td width="15%" className="td-pos" rowSpan={rows.length}>
                  {item.paymenttype_id}
                </Td>

                <Td width="20%" className="td-pos td-pos-last" rowSpan={rows.length}>
                  {item.paymenttypeid_name}
                </Td>
              </>
            )}

            <Td
              width="20%"
              className="td-acc td-acc-first"
              style={{ position: "relative" }}
            >
              <div
                className="row-input-wrapper"
                data-account-dropdown-owner={accountDropdownOwnerRef.current}
                style={{ position: "relative" }}
              >
                <div style={{ display: "grid", gridTemplateColumns: "12ch 1fr 18px", alignItems: "center" }}>
                  <strong title={mapping.name} style={{ display: "block", maxWidth: "12ch", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {mapping.name}
                  </strong>
                  <Input
                    key={`account-id-${mapping.rowKey}`}
                    name={`meal_period_${mapping.mealPeriodKey}_account_id`}
                    className="input-wrapper-row-inputbox"
                    type="text"
                    size="small"
                    value={mapping.accountId}
                    onChange={(e) =>
                      updateMealMapping(mapping.index, {
                        accountId: e.target.value,
                      })
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleUpdate();
                    }}
                  />
                  <span
                    style={{
                      marginLeft: "4px",
                      display: "inline-block",
                      cursor: "pointer",
                      fontSize: 0,
                      width: 0,
                      height: 0,
                      borderLeft: "4px solid transparent",
                      borderRight: "4px solid transparent",
                      borderTop: "5px solid #334155",
                      userSelect: "none",
                    }}
                    onClick={() => {
                      setShowDropdown((prev) =>
                        prev === `meal-${mapping.index}` ? false : `meal-${mapping.index}`
                      );
                      setSearchText("");
                    }}
                  >
                    â·
                  </span>
                </div>

                {showDropdown === `meal-${mapping.index}` && (
                  <div
                    style={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      background: "#fff",
                      zIndex: 1000,
                      boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                      border: "1px solid #ccc",
                      borderRadius: "4px",
                      width: "450px",
                      minWidth: "100%",
                      maxHeight: "200px",
                      overflowY: "auto",
                      marginTop: "4px",
                    }}
                  >
                    <div style={{ padding: "4px" }}>
                      <input
                        ref={searchInputRef}
                        type="text"
                        placeholder="Search..."
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        style={{
                          width: "100%",
                          padding: "6px",
                          fontSize: "14px",
                          border: "1px solid #ccc",
                          borderRadius: "4px",
                        }}
                      />
                    </div>

                    {filteredAccounts.map((acc) => (
                      <div
                        key={`${mapping.index}-${acc.id}-${acc.description}`}
                        style={{
                          padding: "6px 8px",
                          cursor: "pointer",
                          borderBottom: "1px solid #eee",
                        }}
                        onClick={() => {
                          updateMealMapping(mapping.index, {
                            accountId: acc.id,
                            accountDescription: acc.description,
                          });
                          setShowDropdown(false);
                          setSearchText("");
                        }}
                      >
                        <strong>{acc.id}</strong> â€” {acc.description}
                      </div>
                    ))}

                    {filteredAccounts.length === 0 && (
                      <div style={{ padding: "6px 8px", color: "#999" }}>
                        No results
                      </div>
                    )}
                  </div>
                )}
              </div>
            </Td>

            <Td width="20%" className="td-acc" style={{ position: "relative" }}>
              <div className="row-input-wrapper" style={{ position: "relative" }}>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <Input
                    key={`customer-${mapping.rowKey}`}
                    name={`meal_period_${mapping.mealPeriodKey}_qb_customer_id`}
                    className="input-wrapper-row-inputbox"
                    type="text"
                    size="small"
                    value={mapping.customerId}
                    onChange={(e) =>
                      updateMealMapping(mapping.index, {
                        customerId: e.target.value,
                      })
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleUpdate();
                    }}
                  />
                  <span
                    style={{
                      marginLeft: "4px",
                      display: "inline-block",
                      cursor: "pointer",
                      fontSize: 0,
                      width: 0,
                      height: 0,
                      borderLeft: "4px solid transparent",
                      borderRight: "4px solid transparent",
                      borderTop: "5px solid #334155",
                      userSelect: "none",
                    }}
                    onClick={() => {
                      setShowCustomerDropdown((prev) =>
                        prev === `meal-${mapping.index}` ? false : `meal-${mapping.index}`
                      );
                      setCustomerSearchText("");
                    }}
                  >
                    Ã¢ÂÂ·
                  </span>
                </div>

                {showCustomerDropdown === `meal-${mapping.index}` && (
                  <div
                    style={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      background: "#fff",
                      zIndex: 1000,
                      boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                      border: "1px solid #ccc",
                      borderRadius: "4px",
                      width: "450px",
                      minWidth: "100%",
                      maxHeight: "200px",
                      overflowY: "auto",
                      marginTop: "4px",
                    }}
                  >
                    <div style={{ padding: "4px" }}>
                      <input
                        ref={customerSearchInputRef}
                        type="text"
                        placeholder="Search..."
                        value={customerSearchText}
                        onChange={(e) => setCustomerSearchText(e.target.value)}
                        style={{
                          width: "100%",
                          padding: "6px",
                          fontSize: "14px",
                          border: "1px solid #ccc",
                          borderRadius: "4px",
                        }}
                      />
                    </div>

                    {filteredCustomers.map((customer) => (
                      <div
                        key={`${mapping.index}-${customer.id}`}
                        style={{
                          padding: "6px 8px",
                          cursor: "pointer",
                          borderBottom: "1px solid #eee",
                        }}
                        onClick={() => {
                          updateMealMapping(mapping.index, {
                            customerId: String(customer.id),
                          });
                          setShowCustomerDropdown(false);
                          setCustomerSearchText("");
                        }}
                      >
                        <strong>{customer.id}</strong> Ã¢â‚¬â€ {customer.description}
                      </div>
                    ))}

                    {filteredCustomers.length === 0 && (
                      <div style={{ padding: "6px 8px", color: "#999" }}>
                        No results
                      </div>
                    )}
                  </div>
                )}
              </div>
            </Td>

            <Td width="20%" className="td-acc">
              <div className="row-input-wrapper">
                <Input
                  key={`account-description-${mapping.rowKey}`}
                  name={`meal_period_${mapping.mealPeriodKey}_account_description`}
                  className="input-wrapper-row-inputbox"
                  type="text"
                  size="small"
                  value={mapping.accountDescription}
                  onChange={(e) =>
                    updateMealMapping(mapping.index, {
                      accountDescription: e.target.value,
                    })
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleUpdate();
                  }}
                />
              </div>
            </Td>

            <Td width="20%" className="td-acc" style={{ position: "relative", display: hideClassDepartmentColumn ? "none" : undefined }}>
              <div className="row-input-wrapper" style={{ position: "relative" }}>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <Input
                    key={`class-${mapping.rowKey}`}
                    name={`meal_period_${mapping.mealPeriodKey}_class_department_location`}
                    className="input-wrapper-row-inputbox"
                    type="text"
                    size="small"
                    value={mapping.classDepartmentLocation}
                    onChange={(e) =>
                      updateMealMapping(mapping.index, {
                        classDepartmentLocation: e.target.value,
                      })
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleUpdate();
                    }}
                  />
                  {index === 0 && renderD365SettingsButton("main")}
                  <span
                    style={{
                      marginLeft: "4px",
                      display: "inline-block",
                      cursor: "pointer",
                      fontSize: 0,
                      width: 0,
                      height: 0,
                      borderLeft: "4px solid transparent",
                      borderRight: "4px solid transparent",
                      borderTop: "5px solid #334155",
                      userSelect: "none",
                    }}
                    onClick={() => {
                      setShowClassDropdown((prev) =>
                        prev === `meal-${mapping.index}` ? false : `meal-${mapping.index}`
                      );
                      setClassSearchText("");
                    }}
                  >
                    â·
                  </span>
                </div>

                {showClassDropdown === `meal-${mapping.index}` && (
                  <div
                    style={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      background: "#fff",
                      zIndex: 1000,
                      boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                      border: "1px solid #ccc",
                      borderRadius: "4px",
                      width: "450px",
                      minWidth: "100%",
                      maxHeight: "200px",
                      overflowY: "auto",
                      marginTop: "4px",
                    }}
                  >
                    <div style={{ padding: "4px" }}>
                      <input
                        ref={classSearchInputRef}
                        type="text"
                        placeholder="Search..."
                        value={classSearchText}
                        onChange={(e) => setClassSearchText(e.target.value)}
                        style={{
                          width: "100%",
                          padding: "6px",
                          fontSize: "14px",
                          border: "1px solid #ccc",
                          borderRadius: "4px",
                        }}
                      />
                    </div>

                    {filteredClasses.map((cls) => (
                      <div
                        key={`${mapping.index}-${cls.id}`}
                        style={{
                          padding: "6px 8px",
                          cursor: "pointer",
                          borderBottom: "1px solid #eee",
                        }}
                        onClick={() => {
                          updateMealMapping(mapping.index, {
                            classDepartmentLocation: cls.description,
                          });
                          setShowClassDropdown(false);
                          setClassSearchText("");
                        }}
                      >
                        <strong>{cls.id}</strong> â€” {cls.description}
                      </div>
                    ))}

                    {filteredClasses.length === 0 && (
                      <div style={{ padding: "6px 8px", color: "#999" }}>
                        No results
                      </div>
                    )}
                  </div>
                )}
              </div>
            </Td>

            <Td width="20%" className="td-acc">
              <div className="row-input-wrapper">
                <Input
                  key={`over-short-id-${mapping.rowKey}`}
                  name={`meal_period_${mapping.mealPeriodKey}_over_short_id`}
                  className="input-wrapper-row-inputbox"
                  type="text"
                  size="small"
                  value={mapping.overShortId}
                  onChange={(e) =>
                    updateMealMapping(mapping.index, {
                      overShortId: e.target.value,
                    })
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleUpdate();
                  }}
                />
              </div>
            </Td>

            <Td width="20%" className="td-acc">
              <div className="row-input-wrapper">
                <Input
                  key={`over-short-name-${mapping.rowKey}`}
                  name={`meal_period_${mapping.mealPeriodKey}_over_short_name`}
                  className="input-wrapper-row-inputbox"
                  type="text"
                  size="small"
                  value={mapping.overShortName}
                  onChange={(e) =>
                    updateMealMapping(mapping.index, {
                      overShortName: e.target.value,
                    })
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleUpdate();
                  }}
                />
              </div>
            </Td>

            {index === 0 && (
              <Td
                width="10%"
                textAlign="right"
                className="td-acc"
                rowSpan={rows.length}
              >
                <div className="row-input-wrapper-end">
                  {!edit ? (
                    <Button onClick={() => setEdit(true)} size="small" fill="true">
                      Edit
                    </Button>
                  ) : changed ? (
                    <Button onClick={handleUpdate} size="small" fill="true">
                      Update
                    </Button>
                  ) : (
                    <Button
                      onClick={() => {
                        resetValues();
                        setEdit(false);
                      }}
                      size="small"
                      fill="true"
                    >
                      Cancel
                    </Button>
                  )}
                </div>
              </Td>
            )}
          </Tr>
        ))}
      </>
    );
  }

  return (
    <>
    {d365ModalElement}
    <Tr
      textAlign="right"
      className={`${ui.theme === "flame" ? "" : "dark-table-row"}`}
      onDoubleClick={() => {
        resetValues();
        setEdit(true);
      }}
    >
      {/* POS */}
      <Td width="15%" className="td-pos">
        {item.company_id}
      </Td>

      <Td width="15%" className="td-pos">
        {item.paymenttype_id}
      </Td>

      <Td width="20%" className="td-pos td-pos-last">
        {item.paymenttypeid_name}
      </Td>

      {/* ACCOUNTING */}
      <Td
        width="20%"
        className="td-acc td-acc-first"
        style={{ position: "relative" }}
      >
        {!edit ? (
          <div className="row-input-wrapper">
            {inputBox1}
            <div className="flex" />
          </div>
        ) : (
          <div
            className="row-input-wrapper"
            data-account-dropdown-owner={accountDropdownOwnerRef.current}
            style={{ position: "relative" }}
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              <Input
                className="input-wrapper-row-inputbox"
                type="text"
                size="small"
                value={inputBox1}
                onChange={(e) => setInputBox1(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleUpdate();
                }}
              />
              <span
                style={{
                  marginLeft: "4px",
                  display: "inline-block",
                  cursor: "pointer",
                  fontSize: 0,
                  width: 0,
                  height: 0,
                  borderLeft: "4px solid transparent",
                  borderRight: "4px solid transparent",
                  borderTop: "5px solid #334155",
                  userSelect: "none",
                }}
                onClick={() => setShowDropdown((prev) => !prev)}
              >
                ⏷
              </span>
            </div>

            {showDropdown && (
              <div
                style={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  background: "#fff",
                  zIndex: 1000,
                  boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  width: "450px",
                  minWidth: "100%",
                  maxHeight: "200px",
                  overflowY: "auto",
                  marginTop: "4px",
                }}
              >
                <div style={{ padding: "4px" }}>
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder="Search..."
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "6px",
                      fontSize: "14px",
                      border: "1px solid #ccc",
                      borderRadius: "4px",
                    }}
                  />
                </div>

                {filteredAccounts.map((acc) => (
                  <div
                    key={`${acc.id}-${acc.description}`}
                    style={{
                      padding: "6px 8px",
                      cursor: "pointer",
                      borderBottom: "1px solid #eee",
                    }}
                    onClick={() => {
                      setInputBox1(acc.id);
                      setInputBox2(acc.description);
                      setChanged(true);
                      setShowDropdown(false);
                      setSearchText("");
                    }}
                  >
                    <strong>{acc.id}</strong> — {acc.description}
                  </div>
                ))}

                {filteredAccounts.length === 0 && (
                  <div style={{ padding: "6px 8px", color: "#999" }}>
                    No results
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </Td>


      <Td width="20%" className="td-acc">
        {!edit ? (
          <div className="row-input-wrapper">
            {inputBox2}
            <div className="flex" />
          </div>
        ) : (
          <div className="row-input-wrapper">
            <div>
              <Input
                className="input-wrapper-row-inputbox"
                type="text"
                size="small"
                value={inputBox2}
                onChange={(e) => setInputBox2(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleUpdate();
                }}
              />
            </div>
          </div>
        )}
      </Td>

      <Td width="20%" className="td-acc" style={{ position: "relative", display: hideClassDepartmentColumn ? "none" : undefined }}>
        {!edit ? (
          <div className="row-input-wrapper">
            {inputBox5}
            {renderD365SettingsButton("main")}
            <div className="flex" />
          </div>
        ) : (
          <div className="row-input-wrapper" style={{ position: "relative" }}>
            <QuickBooksClassDepartmentField
              value={inputBox5}
              onChange={(value) => {
                setInputBox5(value);
                setChanged(true);
              }}
              classDepartments={classList}
              onEnter={handleUpdate}
            >
              {renderD365SettingsButton("main")}
            </QuickBooksClassDepartmentField>
          </div>
        )}
      </Td>

      <Td width="20%" className="td-acc" style={{ position: "relative" }}>
        {!edit ? (
          <div className="row-input-wrapper">
            {inputBox6}
            <div className="flex" />
          </div>
        ) : (
          <div className="row-input-wrapper" style={{ position: "relative" }}>
            <div style={{ display: "flex", alignItems: "center" }}>
              <Input
                className="input-wrapper-row-inputbox"
                type="text"
                size="small"
                value={inputBox6}
                onChange={(e) => {
                  setInputBox6(e.target.value);
                  setChanged(true);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleUpdate();
                }}
              />
              <span
                style={{
                  marginLeft: "4px",
                  display: "inline-block",
                  cursor: "pointer",
                  fontSize: 0,
                  width: 0,
                  height: 0,
                  borderLeft: "4px solid transparent",
                  borderRight: "4px solid transparent",
                  borderTop: "5px solid #334155",
                  userSelect: "none",
                }}
                onClick={() => {
                  setShowCustomerDropdown((prev) => !prev);
                  setCustomerSearchText("");
                }}
              />
            </div>

            {showCustomerDropdown && (
              <div
                style={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  background: "#fff",
                  zIndex: 1000,
                  boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  width: "450px",
                  minWidth: "100%",
                  maxHeight: "200px",
                  overflowY: "auto",
                  marginTop: "4px",
                }}
              >
                <div style={{ padding: "4px" }}>
                  <input
                    ref={customerSearchInputRef}
                    type="text"
                    placeholder="Search..."
                    value={customerSearchText}
                    onChange={(e) => setCustomerSearchText(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "6px",
                      fontSize: "14px",
                      border: "1px solid #ccc",
                      borderRadius: "4px",
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        const first = filteredCustomers[0];
                        if (first) {
                          setInputBox6(String(first.id));
                          setCustomerId(String(first.id));
                          setChanged(true);
                          setShowCustomerDropdown(false);
                          setCustomerSearchText("");
                        }
                      }
                    }}
                  />
                </div>

                {filteredCustomers.map((customer) => (
                  <div
                    key={customer.id}
                    style={{
                      padding: "6px 8px",
                      cursor: "pointer",
                      borderBottom: "1px solid #eee",
                    }}
                    onClick={() => {
                      setInputBox6(String(customer.id));
                      setCustomerId(String(customer.id));
                      setChanged(true);
                      setShowCustomerDropdown(false);
                      setCustomerSearchText("");
                    }}
                  >
                    <strong>{customer.id}</strong> - {customer.description}
                  </div>
                ))}

                {filteredCustomers.length === 0 && (
                  <div style={{ padding: "6px 8px", color: "#999" }}>
                    No results
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </Td>
      <Td width="20%" className="td-acc">
        {!edit ? (
          <div className="row-input-wrapper">
            {inputBox3}
            <div className="flex" />
          </div>
        ) : (
          <div className="row-input-wrapper">
            <div>
              <Input
                className="input-wrapper-row-inputbox"
                type="text"
                size="small"
                value={inputBox3}
                onChange={(e) => setInputBox3(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleUpdate();
                }}
              />
            </div>
          </div>
        )}
      </Td>

      <Td width="20%" className="td-acc">
        {!edit ? (
          <div className="row-input-wrapper">
            {inputBox4}
            <div className="flex" />
          </div>
        ) : (
          <div className="row-input-wrapper">
            <div>
              <Input
                className="input-wrapper-row-inputbox"
                type="text"
                size="small"
                value={inputBox4}
                onChange={(e) => setInputBox4(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleUpdate();
                }}
              />
            </div>
          </div>
        )}
      </Td>

      <Td width="10%" textAlign="right" className="td-acc">
        {!edit ? (
          <div className="row-input-wrapper-end">
            <Button
              onClick={() => {
                resetValues();
                setEdit(true);
              }}
              size="small"
              fill="true"
            >
              Edit
            </Button>
          </div>
        ) : (
          <div className="row-input-wrapper-end">
            {changed ? (
              <Button onClick={handleUpdate} size="small" fill="true">
                Update
              </Button>
            ) : inputBox1 === "" &&
              inputBox2 === "" &&
              inputBox3 === "" &&
              inputBox4 === "" ? null : (
              <Button
                onClick={() => {
                  resetValues();
                  setEdit(false);
                }}
                size="small"
                fill="true"
              >
                Cancel
              </Button>
            )}
          </div>
        )}
      </Td>
    </Tr>
    </>
  );
};

export default Row;